import { z } from 'zod';
import type { AccessLevel } from './constants';
import type { FilterGroup } from './tasks';

// ---------- Widgets ----------

export const WIDGET_TYPES = [
  'task_count',
  'status_breakdown',
  'tasks_by_assignee',
  'workload',
  'overdue_list',
  'burndown',
  'time_tracked',
  'line_chart',
  'task_list',
  'calculation',
  'text',
  'embed',
  'chat_feed',
  'doc_embed',
] as const;
export type WidgetType = (typeof WIDGET_TYPES)[number];

export interface WidgetMeta {
  type: WidgetType;
  label: string;
  description: string;
  /** Default size in grid units (12 columns). */
  w: number;
  h: number;
}

export const WIDGETS: WidgetMeta[] = [
  { type: 'task_count', label: 'Task count', description: 'How many tasks match a filter', w: 3, h: 2 },
  {
    type: 'status_breakdown',
    label: 'Status breakdown',
    description: 'Tasks by status as a pie or bar chart',
    w: 4,
    h: 4,
  },
  { type: 'tasks_by_assignee', label: 'Tasks by assignee', description: 'Open tasks per person', w: 4, h: 4 },
  { type: 'workload', label: 'Workload', description: 'Load against capacity per person', w: 6, h: 4 },
  { type: 'overdue_list', label: 'Overdue', description: 'Tasks past their due date', w: 4, h: 4 },
  { type: 'burndown', label: 'Burndown', description: 'Remaining or completed work over time', w: 6, h: 4 },
  {
    type: 'time_tracked',
    label: 'Time tracked',
    description: 'Logged time by person, space or billable',
    w: 4,
    h: 4,
  },
  {
    type: 'line_chart',
    label: 'Tasks over time',
    description: 'Created or completed tasks per day or week',
    w: 6,
    h: 4,
  },
  { type: 'task_list', label: 'Task list', description: 'A filtered list of tasks', w: 6, h: 5 },
  { type: 'calculation', label: 'Calculation', description: 'Sum or average of a number field', w: 3, h: 2 },
  { type: 'text', label: 'Text', description: 'Notes, headings, links', w: 4, h: 3 },
  { type: 'embed', label: 'Embed', description: 'A web page in a frame', w: 6, h: 5 },
  { type: 'chat_feed', label: 'Chat feed', description: 'Latest messages from a channel', w: 4, h: 5 },
  { type: 'doc_embed', label: 'Doc', description: 'A doc page, read-only', w: 6, h: 6 },
];

/**
 * What a dashboard covers. `me` is a personal dashboard: the viewer's own tasks across everything
 * they can see. It belongs to one person, cannot be shared and cannot be re-scoped.
 */
export const DASHBOARD_SCOPE_TYPES = ['workspace', 'space', 'folder', 'list', 'me'] as const;
export type DashboardScopeType = (typeof DASHBOARD_SCOPE_TYPES)[number];

/** Where a widget takes its tasks from: the dashboard's scope unless it names its own place. */
export interface WidgetScope {
  scopeType?: 'workspace' | 'space' | 'folder' | 'list';
  scopeId?: string;
}

/** Per widget settings. Keys depend on the type; all are optional so saved widgets never break. */
export interface WidgetConfig extends WidgetScope {
  filter?: FilterGroup;
  includeClosed?: boolean;
  /** status_breakdown */
  chart?: 'pie' | 'bar';
  /** workload */
  measure?: 'hours' | 'count' | 'points';
  /** time_tracked */
  groupBy?: 'user' | 'space' | 'billable' | 'list';
  /** burndown, line_chart */
  metric?: 'remaining' | 'completed' | 'created';
  period?: 'day' | 'week';
  days?: number;
  /** task_list */
  sortBy?: string;
  limit?: number;
  /** calculation */
  fieldKey?: string;
  operation?: 'sum' | 'avg' | 'min' | 'max' | 'count';
  /** text */
  body?: unknown;
  /** embed */
  url?: string;
  /** chat_feed */
  channelId?: string;
  /** doc_embed */
  docId?: string;
  pageId?: string;
  [key: string]: unknown;
}

export interface WidgetDTO {
  id: string;
  dashboardId: string;
  type: WidgetType;
  title: string | null;
  config: WidgetConfig;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardFilters {
  /** Date-only values (UTC midnight) bounding dueDate for task widgets and entry start for time. */
  from?: number | null;
  to?: number | null;
  spaceIds?: string[];
  assigneeIds?: string[];
}

export interface DashboardDTO {
  id: string;
  name: string;
  icon: string | null;
  scopeType: DashboardScopeType;
  /** The place's id, the workspace id, or for a personal dashboard the owner's user id. */
  scopeId: string;
  isPrivate: boolean;
  ownerId: string;
  filters: DashboardFilters;
  access: AccessLevel;
  createdAt: number;
  updatedAt: number;
  widgets: WidgetDTO[];
}

export interface DashboardChangedEventData {
  dashboardId: string;
  op: 'created' | 'updated' | 'deleted' | 'widgets';
}

// ---------- Presets ----------

/** A widget as a preset lays it out: type, title, settings and grid position. */
export interface PresetWidget {
  type: WidgetType;
  title: string;
  config?: WidgetConfig;
  x: number;
  y: number;
  w: number;
  h: number;
}

export const DASHBOARD_PRESET_KEYS = [
  'blank',
  'my_work',
  'workspace_overview',
  'team_workload',
  'place_overview',
  'time_report',
] as const;
export type DashboardPresetKey = (typeof DASHBOARD_PRESET_KEYS)[number];

export interface DashboardPreset {
  key: DashboardPresetKey;
  name: string;
  description: string;
  /**
   * What the preset covers. `me`: the viewer's own work. `workspace`: everything, which only
   * admins may create. `location`: a space, folder or list the person picks. `any`: the person
   * chooses among what their role allows.
   */
  scope: 'me' | 'workspace' | 'location' | 'any';
  widgets: PresetWidget[];
}

const estimate: PresetWidget = {
  type: 'calculation',
  title: 'Estimated work',
  config: { fieldKey: 'timeEstimate', operation: 'sum' },
  x: 3,
  y: 0,
  w: 3,
  h: 2,
};

const completedPerDay = (x: number, y: number, days: number): PresetWidget => ({
  type: 'line_chart',
  title: `Completed, last ${days} days`,
  config: { metric: 'completed', period: 'day', days },
  x,
  y,
  w: 6,
  h: 4,
});

export const DASHBOARD_PRESETS: DashboardPreset[] = [
  {
    key: 'blank',
    name: 'Blank',
    description: 'Start empty and add the widgets you want.',
    scope: 'any',
    widgets: [],
  },
  {
    key: 'my_work',
    name: 'My dashboard',
    description: 'Your open tasks, what is overdue, what is next and what you finished.',
    scope: 'me',
    widgets: [
      { type: 'task_count', title: 'Open tasks', x: 0, y: 0, w: 3, h: 2 },
      estimate,
      { type: 'status_breakdown', title: 'By status', config: { chart: 'pie' }, x: 6, y: 0, w: 6, h: 4 },
      { type: 'overdue_list', title: 'Overdue', x: 0, y: 2, w: 6, h: 4 },
      {
        type: 'task_list',
        title: 'Up next',
        config: { sortBy: 'dueDate', limit: 10 },
        x: 6,
        y: 4,
        w: 6,
        h: 5,
      },
      completedPerDay(0, 6, 14),
    ],
  },
  {
    key: 'workspace_overview',
    name: 'Workspace overview',
    description: 'Every open task across the workspace: by status, by person, overdue and over time.',
    scope: 'workspace',
    widgets: [
      { type: 'task_count', title: 'Open tasks', x: 0, y: 0, w: 3, h: 2 },
      estimate,
      { type: 'status_breakdown', title: 'By status', config: { chart: 'pie' }, x: 6, y: 0, w: 6, h: 4 },
      { type: 'tasks_by_assignee', title: 'Open tasks by person', x: 0, y: 2, w: 6, h: 4 },
      {
        type: 'workload',
        title: 'Workload, next 7 days',
        config: { measure: 'hours' },
        x: 6,
        y: 4,
        w: 6,
        h: 4,
      },
      { type: 'overdue_list', title: 'Overdue', x: 0, y: 6, w: 6, h: 5 },
      completedPerDay(6, 8, 30),
      {
        type: 'line_chart',
        title: 'Created, last 30 days',
        config: { metric: 'created', period: 'day', days: 30 },
        x: 0,
        y: 11,
        w: 6,
        h: 4,
      },
    ],
  },
  {
    key: 'team_workload',
    name: 'Team workload',
    description: 'Who has what on their plate this week, time logged, and what is slipping.',
    scope: 'workspace',
    widgets: [
      {
        type: 'workload',
        title: 'Workload, next 7 days',
        config: { measure: 'hours' },
        x: 0,
        y: 0,
        w: 12,
        h: 5,
      },
      { type: 'tasks_by_assignee', title: 'Open tasks by person', x: 0, y: 5, w: 6, h: 5 },
      {
        type: 'time_tracked',
        title: 'Time tracked by person',
        config: { groupBy: 'user' },
        x: 6,
        y: 5,
        w: 6,
        h: 5,
      },
      { type: 'overdue_list', title: 'Overdue', x: 0, y: 10, w: 12, h: 5 },
    ],
  },
  {
    key: 'place_overview',
    name: 'Client or project overview',
    description: 'One space, folder or list: status, remaining work, overdue tasks and what is next.',
    scope: 'location',
    widgets: [
      { type: 'task_count', title: 'Open tasks', x: 0, y: 0, w: 3, h: 2 },
      estimate,
      { type: 'status_breakdown', title: 'By status', config: { chart: 'pie' }, x: 6, y: 0, w: 6, h: 4 },
      {
        type: 'burndown',
        title: 'Remaining work, last 30 days',
        config: { metric: 'remaining', days: 30 },
        x: 0,
        y: 2,
        w: 6,
        h: 4,
      },
      { type: 'overdue_list', title: 'Overdue', x: 6, y: 4, w: 6, h: 4 },
      {
        type: 'task_list',
        title: 'Up next',
        config: { sortBy: 'dueDate', limit: 10 },
        x: 0,
        y: 6,
        w: 6,
        h: 5,
      },
      completedPerDay(6, 8, 30),
    ],
  },
  {
    key: 'time_report',
    name: 'Time report',
    description: 'Time logged in the last 30 days by person, space, list and billable.',
    scope: 'workspace',
    widgets: [
      {
        type: 'calculation',
        title: 'Time tracked on tasks',
        config: { fieldKey: 'timeTracked', operation: 'sum', includeClosed: true },
        x: 0,
        y: 0,
        w: 3,
        h: 2,
      },
      { type: 'time_tracked', title: 'By space', config: { groupBy: 'space' }, x: 6, y: 0, w: 6, h: 5 },
      { type: 'time_tracked', title: 'By person', config: { groupBy: 'user' }, x: 0, y: 2, w: 6, h: 5 },
      { type: 'time_tracked', title: 'Billable', config: { groupBy: 'billable' }, x: 6, y: 5, w: 6, h: 4 },
      { type: 'time_tracked', title: 'By list', config: { groupBy: 'list' }, x: 0, y: 7, w: 6, h: 5 },
    ],
  },
];

export function dashboardPreset(key: DashboardPresetKey): DashboardPreset {
  return DASHBOARD_PRESETS.find((p) => p.key === key)!;
}

/** Whether a preset can be built on a scope: fixed presets must match, `location` needs a place. */
export function presetFitsScope(preset: DashboardPreset, scopeType: DashboardScopeType): boolean {
  if (preset.scope === 'any') return true;
  if (preset.scope === 'location') return scopeType !== 'workspace' && scopeType !== 'me';
  return preset.scope === scopeType;
}

// ---------- Schemas ----------

const id = z.string().min(1).max(64);
const scopeType = z.enum(DASHBOARD_SCOPE_TYPES);

export const dashboardFiltersSchema = z.object({
  from: z.number().int().nullable().optional(),
  to: z.number().int().nullable().optional(),
  spaceIds: z.array(id).max(50).optional(),
  assigneeIds: z.array(id).max(50).optional(),
});

export const createDashboardSchema = z.object({
  name: z.string().trim().min(1).max(120),
  icon: z.string().max(16).nullable().optional(),
  scopeType: scopeType.default('workspace'),
  scopeId: id.optional(),
  isPrivate: z.boolean().optional(),
  /** Starts the dashboard with the preset's widgets. A fixed-scope preset decides the scope. */
  preset: z.enum(DASHBOARD_PRESET_KEYS).optional(),
});
export type CreateDashboardInput = z.infer<typeof createDashboardSchema>;

export const updateDashboardSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  icon: z.string().max(16).nullable().optional(),
  scopeType: scopeType.optional(),
  scopeId: id.optional(),
  isPrivate: z.boolean().optional(),
  filters: dashboardFiltersSchema.optional(),
});
export type UpdateDashboardInput = z.infer<typeof updateDashboardSchema>;

const layout = {
  x: z.number().int().min(0).max(11),
  y: z.number().int().min(0).max(10_000),
  w: z.number().int().min(1).max(12),
  h: z.number().int().min(1).max(40),
};

export const createWidgetSchema = z.object({
  type: z.enum(WIDGET_TYPES),
  title: z.string().trim().max(120).nullable().optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  x: layout.x.optional(),
  y: layout.y.optional(),
  w: layout.w.optional(),
  h: layout.h.optional(),
});
export type CreateWidgetInput = z.infer<typeof createWidgetSchema>;

export const updateWidgetSchema = z.object({
  title: z.string().trim().max(120).nullable().optional(),
  config: z.record(z.string(), z.unknown()).optional(),
  x: layout.x.optional(),
  y: layout.y.optional(),
  w: layout.w.optional(),
  h: layout.h.optional(),
});
export type UpdateWidgetInput = z.infer<typeof updateWidgetSchema>;

/** Moves and resizes of several widgets at once (a drag reflows its neighbours). */
export const layoutSchema = z.object({
  items: z
    .array(z.object({ id, ...layout }))
    .min(1)
    .max(100),
});
export type LayoutInput = z.infer<typeof layoutSchema>;

/** Aggregated time entries for dashboard widgets. */
export interface TimeReportRow {
  userId: string;
  taskId: string | null;
  listId: string | null;
  spaceId: string | null;
  durationMs: number;
  billable: boolean;
  start: number;
}

export const timeReportQuerySchema = z.object({
  from: z.coerce.number().int(),
  to: z.coerce.number().int(),
});
